import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useTrip, tripService, updateTrip, deleteTrip, app, WaitingIndicator } from '@trip-planner-monorepo/shared';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FaPlus, FaTrash, FaEdit, FaCog, FaSignOutAlt } from 'react-icons/fa';
import TripManagementModal from './TripManagementModal';
import './TripsList.css';

function TripsList() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewTripModal, setShowNewTripModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newTripName, setNewTripName] = useState('');
  const [editingTrip, setEditingTrip] = useState(null);
  const [managementTrip, setManagementTrip] = useState(null);
  const [showManagementModal, setShowManagementModal] = useState(false);

  const { currentUser } = useAuth();
  const { selectedTripId, selectTrip } = useTrip();
  const navigate = useNavigate();

  const fetchTrips = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const allTrips = await tripService.fetchTrips(currentUser.uid);
      setTrips(allTrips);
    } catch (err) {
      setError('Failed to load trips. Please try again.');
      console.error('Error fetching trips:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    if (!newTripName.trim()) {
      setError('Please enter a trip name');
      return;
    }

    try {
      const tripData = {
        name: newTripName.trim(),
        startDate: null,
        endDate: null,
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString()
      };

      const tripId = await tripService.createTrip(currentUser.uid, tripData);
      setShowNewTripModal(false);
      setNewTripName('');
      selectTrip(tripId);
      await fetchTrips();
    } catch (err) {
      setError('Failed to create trip. Please try again.');
      console.error('Error creating trip:', err);
    }
  };

  const handleEditTrip = async (e) => {
    e.preventDefault();
    if (!editingTrip || !editingTrip.name.trim()) {
      setError('Please enter a trip name');
      return;
    }

    try {
      await updateTrip(currentUser.uid, editingTrip.id, {
        name: editingTrip.name.trim()
      });
      setShowEditModal(false);
      setEditingTrip(null);
      await fetchTrips();
    } catch (err) {
      setError('Failed to update trip. Please try again.');
      console.error('Error updating trip:', err);
    }
  };

  const handleDeleteTrip = async (tripId) => {
    if (window.confirm('Are you sure you want to delete this trip? This action cannot be undone.')) {
      try {
        // Clean up shared_trips pointers for all shared users first
        const functions = getFunctions(app, 'us-central1');
        const cleanupFn = httpsCallable(functions, 'cleanupSharedTrip');
        await cleanupFn({ tripId });

        await deleteTrip(currentUser.uid, tripId);
        if (selectedTripId === tripId) {
          selectTrip(null);
        }
        await fetchTrips();
        // Close management modal if open
        if (managementTrip && managementTrip.id === tripId) {
          setShowManagementModal(false);
          setManagementTrip(null);
        }
      } catch (err) {
        setError('Failed to delete trip. Please try again.');
        console.error('Error deleting trip:', err);
      }
    }
  };

  const handleLeaveTrip = async (trip) => {
    if (!window.confirm(`Leave "${trip.name}"? This will only remove the trip from your list — the trip itself won't be deleted.`)) return;
    try {
      const functions = getFunctions(app, 'us-central1');
      const removeSharedUserFn = httpsCallable(functions, 'removeSharedUser');
      await removeSharedUserFn({
        tripId: trip.id,
        tripOwnerId: trip.sharedBy,
        targetUid: currentUser.uid
      });
      if (selectedTripId === trip.id) {
        selectTrip(null);
      }
      await fetchTrips();
    } catch (err) {
      setError('Failed to leave trip. Please try again.');
      console.error('Error leaving trip:', err);
    }
  };

  const startEditTrip = (trip) => {
    setEditingTrip({ ...trip });
    setShowEditModal(true);
  };

  const handleTripClick = (trip) => {
    selectTrip(trip.id, trip.isShared ? trip.sharedBy : null);
    navigate(`/trips/${trip.id}`);
  };

  const handleManageTrip = (trip) => {
    setManagementTrip(trip);
    setShowManagementModal(true);
  };

  return (
    <div className="trips-content">
      {error && <div className="error-message">{error}</div>}

      <div className="trips-header">
        <button
          className="new-trip-button"
          onClick={() => setShowNewTripModal(true)}
        >
          <FaPlus /> New Trip
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '60px 0' }}>
          <WaitingIndicator message="Loading your trips..." />
        </div>
      ) : (
        <div className="trips-grid">
          {trips.length === 0 ? (
            <div className="text-center text-gray-500">
              No trips found. Click &ldquo;New Trip&rdquo; to get started.
            </div>
          ) : (
            <>
              {/* eslint-disable indent */}
              {trips.map(trip => (
                <div key={trip.id} className={`trip-card ${selectedTripId === trip.id ? 'selected' : ''}`}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleTripClick(trip)}
                  onKeyDown={(e) => e.key === 'Enter' && handleTripClick(trip)}
                  className="trip-card-content"
                >
                  <h2>
                    {trip.name}
                    {trip.isShared && <span style={{ fontSize: '0.7em', marginLeft: '8px', color: '#666' }}>(Shared)</span>}
                  </h2>
                  <div className="trip-dates">
                    {trip.startDate ? (
                      <>
                        <span>{new Date(trip.startDate).toLocaleDateString()}</span>
                        {trip.endDate && trip.endDate !== trip.startDate && (
                          <>
                            <span> - </span>
                            <span>{new Date(trip.endDate).toLocaleDateString()}</span>
                          </>
                        )}
                      </>
                    ) : (
                      <span>No dates set</span>
                    )}
                  </div>
                </div>
                <div className="trip-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleManageTrip(trip);
                    }}
                    className="manage-button"
                    title="Manage trip"
                  >
                    <FaCog />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditTrip(trip);
                    }}
                    className="edit-button"
                    title="Edit trip name"
                  >
                    <FaEdit />
                  </button>
                  {trip.isShared ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLeaveTrip(trip);
                      }}
                      className="delete-button"
                      title="Leave shared trip"
                    >
                      <FaSignOutAlt />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this trip?')) {
                          handleDeleteTrip(trip.id);
                        }
                      }}
                      className="delete-button"
                      title="Delete trip"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>
              ))}
              {/* eslint-enable indent */}
            </>
          )}
        </div>
      )}

      {showNewTripModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Create New Trip</h2>
            <form onSubmit={handleCreateTrip}>
              <div className="form-group">
                <label htmlFor="tripName">Trip Name<span className="required">*</span></label>
                <input
                  id="tripName"
                  type="text"
                  value={newTripName}
                  onChange={(e) => setNewTripName(e.target.value)}
                  placeholder="Enter trip name"
                  required
                />
              </div>
              <div className="modal-buttons">
                <button type="submit">Create Trip</button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewTripModal(false);
                    setNewTripName('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingTrip && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Edit Trip</h2>
            <form onSubmit={handleEditTrip}>
              <div className="form-group">
                <label htmlFor="editTripName">Trip Name<span className="required">*</span></label>
                <input
                  id="editTripName"
                  type="text"
                  value={editingTrip.name}
                  onChange={(e) => setEditingTrip({ ...editingTrip, name: e.target.value })}
                  placeholder="Enter trip name"
                  required
                />
              </div>
              <div className="modal-buttons">
                <button type="submit">Save</button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTrip(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <TripManagementModal
        isOpen={showManagementModal}
        onRequestClose={() => {
          setShowManagementModal(false);
          setManagementTrip(null);
        }}
        trip={managementTrip}
        onDelete={handleDeleteTrip}
      />
    </div>
  );
}

export default TripsList;